import React from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PartCodeSelector from '../psi/PartCodeSelector';
import PCBModuleList from './PCBModuleList';
import AssembledModuleList from './AssembledModuleList';
import FinalProductList from './FinalProductList';
import { DigiKeyAuth } from './DigiKeyAuth';

type TabType = 'parts' | 'pcb-modules' | 'assembled-modules' | 'products';

const BOMPage: React.FC = () => {
  const { t } = useTranslation('bom');
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  // Default to 'parts' if no tab specified
  const activeTab = (tab as TabType) || 'parts';

  // Redirect to /app/bom/parts if accessing /app/bom without a tab
  if (!tab) {
    return <Navigate to="/app/bom/parts" replace />;
  }

  const handleTabChange = (tabId: TabType) => {
    navigate(`/app/bom/${tabId}`);
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'parts', label: t('tabs.parts', 'Parts') },
    { id: 'pcb-modules', label: t('tabs.pcbModules', 'PCB Modules') },
    { id: 'assembled-modules', label: t('tabs.assembledModules', 'Assembled Modules') },
    { id: 'products', label: t('tabs.products', 'Products') },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'parts':
        return <PartCodeSelector />;
      case 'pcb-modules':
        return <PCBModuleList />;
      case 'assembled-modules':
        return <AssembledModuleList />;
      case 'products':
        return <FinalProductList />;
      default:
        return <PCBModuleList />;
    }
  };

  return (
    <div className="p-3 sm:p-6">
      {/* Header with DigiKey Auth */}
      <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">BOM</h1>
        <DigiKeyAuth />
      </div>

      {/* Tab Navigation - mobile scrollable */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
        <nav className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory -mb-px" aria-label="Tabs">
          {tabs.map((tabItem) => (
            <button
              key={tabItem.id}
              onClick={() => handleTabChange(tabItem.id)}
              className={`
                snap-start flex-shrink-0 py-3 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-[44px]
                ${activeTab === tabItem.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}
              `}
              aria-current={activeTab === tabItem.id ? 'page' : undefined}
            >
              {tabItem.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default BOMPage;
